import Button from "@/components/bricks/button/Button";
import React, { MouseEventHandler, ReactNode, useState } from "react";
import { createColumnEditorTrigger } from "../../util/table";
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "../dropdown/DropdownMenu";
import EditIcon from "../icons/EditIcon";
import MoreVertIcon from "../icons/MoreVertIcon";
import { ModalProvider } from "../Modal/Modal";
import FormModal from "../Modal/modals/FormModal";
import OpretNyButtonBlock from "../pages/person/id/OpretNyButtonBlock";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import CloseIcon from "../icons/CloseIcon";
import { IColumn } from "../../interfaces/table";

export const TableRoot = (props: {
  children: React.ReactNode;
  className?: string;
  fixedHeight?: boolean;
}) => {
  const { children, className, fixedHeight } = props;
  return (
    <div
      className={`table-responsive${fixedHeight ? " table--fixed-height" : ""}`}
    >
      <table className={`table ${className ?? ""}`}>{children}</table>
    </div>
  );
};

export const TableHead = (props: {
  children: ReactNode;
  className?: string;
  ColumnEditorTrigger?: ReactNode;
}) => {
  const { children, className, ColumnEditorTrigger } = props;

  return (
    <thead className={className ?? ""}>
      <tr>
        {children}
        <th scope="col" className="edit-column">
          {ColumnEditorTrigger}
        </th>
      </tr>
    </thead>
  );
};
export const TableBody = (props: {
  children: ReactNode;
  className?: string;
}) => {
  const { children, className } = props;

  return <tbody className={className ?? ""}>{children}</tbody>;
};

function EditCell<U, V = U>(props: {
  editConfig?: FormConfig<U, V>;
  invalidateData?: InvalidateDataFunction<any>;
}) {
  const { editConfig, invalidateData } = props;
  const [method, setMethod] = useState<FormMethod>("patch");
  if (!editConfig || (!editConfig.patch && !editConfig.delete)) return null;
  return (
    <FormModal
      editConfig={editConfig}
      invalidateData={invalidateData}
      method={method}
      setMethod={setMethod}
      triggerProps={{
        children: <EditIcon />,
        icon: true,
        transparent: true,
        color: "primary",
        title: "Rediger række",
      }}
    />
  );
}

function CancelCell<U, V = U>(props: {
  editConfig?: FormConfig<U, V>;
  invalidateData?: InvalidateDataFunction<any>;
}) {
  const { editConfig, invalidateData } = props;
  const [method, setMethod] = useState<FormMethod>("post");
  if (!editConfig) return null;
  return (
    <FormModal
      title="Afmeld"
      editConfig={editConfig}
      invalidateData={invalidateData}
      method={method}
      setMethod={setMethod}
      triggerProps={{
        children: <CloseIcon height={16} />,
        icon: true,
        transparent: true,
        color: "primary",
        title: "Afmeld",
      }}
    />
  );
}

function StickyCell<U, V = U>(props: {
  editConfig?: FormConfig<U, V>;
  moreActions?: ReactNode[];
  rowAction?: ReactNode;
  invalidateData?: InvalidateDataFunction<any>;
  action?: () => void;
  cancelConfig?: FormConfig<U, V>;
}) {
  const {
    editConfig,
    moreActions,
    rowAction,
    invalidateData,
    action,
    cancelConfig,
  } = props;
  if (!editConfig && !moreActions && !rowAction) return <td></td>;

  return (
    <td className="edit-column">
      <div
        className="d-flex align-items-center justify-content-end gap-2"
        onClick={action}
      >
        {moreActions && (
          <DropdownMenuRoot>
            {moreActions.length > 0 ? (
              <DropdownMenuTrigger>
                <Button
                  icon
                  transparent
                  aria-label="Handlinger"
                  className="tooltip-center"
                  color="primary"
                >
                  <MoreVertIcon height={16} />
                </Button>
              </DropdownMenuTrigger>
            ) : null}

            <DropdownMenuContent align="end">{moreActions}</DropdownMenuContent>
          </DropdownMenuRoot>
        )}
        {rowAction}
        <EditCell editConfig={editConfig} invalidateData={invalidateData} />
        {cancelConfig ? (
          <CancelCell
            editConfig={cancelConfig}
            invalidateData={invalidateData}
          />
        ) : null}
      </div>
    </td>
  );
}
export function TableRow<U, V = U>(props: {
  children: ReactNode;
  className?: string;
  moreActions?: ReactNode[];
  rowAction?: ReactNode;
  editConfig?: FormConfig<U, V>;
  invalidateData?: InvalidateDataFunction<any>;
  onClick?: MouseEventHandler<HTMLTableRowElement>;
  action?: () => void;
  cancelConfig?: FormConfig<any>;
}) {
  const {
    onClick,
    children,
    className,
    editConfig,
    moreActions,
    rowAction,
    invalidateData,
    action,
    cancelConfig,
  } = props;

  return (
    <tr className={className ?? ""} onClick={onClick ? onClick : () => {}}>
      {children}
      <StickyCell
        editConfig={editConfig}
        moreActions={moreActions}
        rowAction={rowAction}
        invalidateData={invalidateData}
        action={action}
        cancelConfig={cancelConfig}
      />
    </tr>
  );
}

export default function TableBasic<T, U>(props: {
  children: ReactNode;
  headerRow: ReactNode;
  defaultColumns: IColumn<T>[];
  columns: IColumn<T>[];
  setActiveColumns: React.Dispatch<React.SetStateAction<T[]>>;
  editConfig?: FormConfig<U>;
  invalidateData?: InvalidateDataFunction<any>;
  className?: string;
}) {
  const {
    children,
    columns,
    headerRow,
    setActiveColumns,
    defaultColumns,
    editConfig,
    invalidateData,
    className,
  } = props;
  return (
    <>
      <TableRoot className={className}>
        <ModalProvider>
          <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
            {headerRow}
          </TableHead>
          <ColumnEditorModal
            columns={columns}
            setActiveColumns={setActiveColumns}
            defaultColumns={defaultColumns}
          />
        </ModalProvider>
        <TableBody>{children}</TableBody>
      </TableRoot>

      <OpretNyButtonBlock
        editConfig={editConfig}
        invalidateData={invalidateData}
      />
    </>
  );
}
